import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconLock, IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { DataTable, FilterBar, PageHeader, useSearchParamState } from "@how/ui";
import { CareDrawer } from "./CareDrawer";
import {
  CARE_STATUSES,
  CARE_STATUS_COLOR,
  CARE_STATUS_LABEL,
  useCareQueue,
  useLogCare,
  usePeopleOptions,
  type CareRow,
} from "./care-queries";
import { semanticColor } from "@how/design-system";

const columns: ColumnDef<CareRow>[] = [
  {
    accessorKey: "createdAt",
    header: "When",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm">
        {new Date(ctx.row.original.createdAt).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
        })}
      </Text>
    ),
  },
  {
    accessorKey: "lastName",
    header: "Who",
    enableSorting: false,
    cell: (ctx) => (
      <div>
        <Text size="sm" fw={600}>
          {ctx.row.original.firstName} {ctx.row.original.lastName}
        </Text>
        {ctx.row.original.household === null ? null : (
          <Text size="xs" c="dimmed">
            {ctx.row.original.household}
          </Text>
        )}
      </div>
    ),
  },
  {
    accessorKey: "body",
    header: "Asked for",
    enableSorting: false,
    cell: (ctx) => (
      <Group gap={6} wrap="nowrap">
        {ctx.row.original.confidential ? (
          <Badge
            variant="light"
            color={semanticColor("special")}
            leftSection={<IconLock size={10} />}
          >
            Confidential
          </Badge>
        ) : null}
        <Text size="sm" lineClamp={1}>
          {ctx.row.original.body}
        </Text>
      </Group>
    ),
  },
  {
    accessorKey: "mayContact",
    header: "Contact",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" {...(ctx.row.original.mayContact ? {} : { c: "dimmed" })}>
        {ctx.row.original.mayContact ? "Yes" : "Prayer only"}
      </Text>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
    cell: (ctx) => (
      <div>
        <Badge variant="light" color={CARE_STATUS_COLOR[ctx.row.original.status]}>
          {CARE_STATUS_LABEL[ctx.row.original.status]}
        </Badge>
        {ctx.row.original.ownerName === null ? null : (
          <Text size="xs" c="dimmed">
            {ctx.row.original.ownerName}
          </Text>
        )}
      </div>
    ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 500 };
const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  ...CARE_STATUSES.map((s) => ({ value: s, label: CARE_STATUS_LABEL[s] })),
];

/**
 * Care (task 15, D13): the queue of requests the caller may read, open first. A row opens the request to take it,
 * hand it on, move it along or close it with a note; Log a request is for someone who asked in person.
 */
export function CarePage() {
  const canManage = usePermission("care.manage", { write: true });
  const [statusParam, setStatusParam] = useSearchParamState("status", "open");
  const [selected, setSelected] = useSearchParamState("request", "");
  const [logging, setLogging] = useState(false);
  const status = CARE_STATUSES.find((s) => s === statusParam) ?? null;
  const queue = useCareQueue(status);
  const rows = (queue.data ?? []).filter((r) => statusParam !== "open" || r.status !== "closed");
  const tableStatus = queue.isPending ? "loading" : queue.isError ? "error" : "ready";
  const current =
    rows.find((r) => r.id === selected) ??
    (queue.data ?? []).find((r) => r.id === selected) ??
    null;
  return (
    <>
      <PageHeader
        title="Care"
        description="Prayer and help people have asked for. Take one, look after it, close it with a note."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setLogging(true);
              }}
            >
              Log a request
            </Button>
          ) : undefined
        }
      />
      <FilterBar
        search=""
        onSearchChange={() => undefined}
        searchPlaceholder="Search"
        filters={
          <Select
            aria-label="Filter by status"
            data={STATUS_OPTIONS}
            value={statusParam}
            allowDeselect={false}
            onChange={(value) => {
              setStatusParam(value === null || value === "open" ? undefined : value);
            }}
            w={180}
          />
        }
      />
      <DataTable
        label="Care requests"
        columns={columns}
        rows={rows}
        totalRows={rows.length}
        status={tableStatus}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setSelected(row.id);
        }}
        emptyTitle={statusParam === "open" ? "Nothing open" : "Nothing here"}
        emptyDescription="When someone asks for prayer or help in the portal, it appears here."
        errorMessage="Could not load the care queue."
        onRetry={() => {
          void queue.refetch();
        }}
      />
      <CareDrawer
        request={current}
        canManage={canManage.data === true}
        onClose={() => {
          setSelected(undefined);
        }}
      />
      <LogModal
        key={logging ? "open" : "closed"}
        opened={logging}
        onClose={() => {
          setLogging(false);
        }}
        onLogged={() => {
          setLogging(false);
        }}
      />
    </>
  );
}

function LogModal({
  opened,
  onClose,
  onLogged,
}: {
  opened: boolean;
  onClose: () => void;
  onLogged: () => void;
}) {
  const log = useLogCare();
  const canConfidential = usePermission("care.read_confidential");
  const [search, setSearch] = useState("");
  const people = usePeopleOptions(search);
  const [personId, setPersonId] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [mayContact, setMayContact] = useState(true);
  const [confidential, setConfidential] = useState(false);
  const code = (log.error as { code?: string } | null)?.code;
  const errorText =
    code === "42501"
      ? "A confidential request needs care.read_confidential."
      : log.isError
        ? "Not logged. Check your access and try again."
        : null;
  return (
    <Modal opened={opened} onClose={onClose} title="Log a request" radius="lg">
      <Stack gap="sm">
        <Select
          label="Who asked"
          placeholder="Type a name"
          searchable
          searchValue={search}
          onSearchChange={setSearch}
          data={people.data ?? []}
          filter={({ options: all }) => all}
          value={personId}
          onChange={setPersonId}
          nothingFoundMessage={search.length < 2 ? "Type two letters" : "Nobody by that name"}
        />
        <Textarea
          label="What they asked for"
          autosize
          minRows={3}
          maxLength={4000}
          value={body}
          onChange={(e) => {
            setBody(e.currentTarget.value);
          }}
        />
        <Checkbox
          label="They are happy to be contacted"
          checked={mayContact}
          onChange={(e) => {
            setMayContact(e.currentTarget.checked);
          }}
        />
        {canConfidential.data === true ? (
          <Checkbox
            label="Confidential: only those who may read confidential requests"
            checked={confidential}
            onChange={(e) => {
              setConfidential(e.currentTarget.checked);
            }}
          />
        ) : null}
        {errorText !== null ? (
          <Text size="sm" c="red">
            {errorText}
          </Text>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={personId === null || body.trim() === ""}
            loading={log.isPending}
            onClick={() => {
              if (personId === null) return;
              log.mutate({ personId, body, mayContact, confidential }, { onSuccess: onLogged });
            }}
          >
            Log
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
